import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isFunction from 'lodash/isFunction';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { AddQuantityButton } from '../../components/ProductCard/ProductCard';
import MultiSelectDropdown, {
  useMultiSelectDropdown,
  multiSelectType,
} from '../../components/MultiSelectDropdown';
import Button from '../../components/Button';
import { ProductSwapTypes } from '../../utils/constants';
import { getVariantFromSelectedOptions } from '../utils';
import { useGlobalContext } from '../../subscriptions/state/GlobalContext';
import { Paragraph } from '../../components/Typography';
import { PRODUCT_CHANGE_OPTIONS } from '../../utils/constants';
import useProductContext, { ProductContext } from '../state/ProductContext';
import {
  isFeatureFlagEnabled,
  FEATURE_FLAGS,
} from '../../../widgetSrc/utils/featureFlags';

interface Props {
  product: GlobalProduct;
  selectedVariant?: GlobalVariant;
  isDecreaseDisabled?: boolean | ((quantity: number) => boolean);
  isIncreaseDisabled?: boolean;
  onSelect: (selectedAddon: { id: string; quantity: number }) => void;
  onConfirm: (selectedOption?: MultiSelectOption) => void;
  openModal: (productType: ProductSwapTypes) => void;
  hasVariants: boolean;
}

const VariantSliderSelector = ({
  product,
  selectedVariant,
  onSelect,
  onConfirm,
  openModal,
  hasVariants,
  isDecreaseDisabled = () => false,
  isIncreaseDisabled = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    options,
    quantity,
    setIsOpen,
    handleOnOptionClicked,
    handleClose,
    openProductSwap,
  } = useVariantSliderSelector(
    product,
    selectedVariant,
    onSelect,
    onConfirm,
    openModal,
  );

  const { product: line } = useProductContext() as ProductContext;
  const isShuffle =
    line?.CustomAttributes?.find(({ key }) => key === '_bundleType')?.value ===
    'shuffle';

  const { customerPortalSettings, ignoreBaBOutOfStockAllowance } =
    useGlobalContext();
  const isBaBIgnoreOutOfStockEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.LAUNCH_BAB_IGNORE_OUT_OF_STOCK,
  );

  const disableDecreaseButton = () => {
    return isFunction(isDecreaseDisabled)
      ? isDecreaseDisabled(quantity)
      : isDecreaseDisabled;
  };

  const isOutOfStock =
    (selectedVariant?.inventory?.inventoryQuantity <= 0 &&
      selectedVariant?.inventory?.inventoryPolicy === 'DENY' &&
      selectedVariant?.inventory?.inventoryManagement !== 'NOT_MANAGED') ||
    (isBaBIgnoreOutOfStockEnabled &&
      ignoreBaBOutOfStockAllowance &&
      selectedVariant?.inventory?.inventoryQuantity <= 0 &&
      selectedVariant?.inventory?.inventoryPolicy === 'CONTINUE');

  const isAvailable = selectedVariant?.available && !isOutOfStock;
  const isPrepaidV2 = !!product.isPrepaidV2;

  return (
    <SliderFooter>
      {hasVariants ? (
        <MultiSelectDropdown
          isOpen={isOpen}
          options={options}
          onClose={handleClose}
          onOptionClicked={handleOnOptionClicked}
          buttonText={t('addons.save')}
          onSelectClick={() => setIsOpen(!isOpen)}
          selectedOption={selected?.label}
          onConfirm={handleConfirm}
          label={t('addons.type')}
          withButton={hasVariants}
          helperText={
            !selected?.id || !selectedVariant?.available
              ? t('addons.selection_unavailable')
              : ''
          }
          error={!selectedVariant?.available || !selected?.id}
        />
      ) : null}

      <QuantitySelector>
        <AddQuantityButton
          style={{ width: '45%' }}
          onIncrease={increase}
          onDecrease={decrease}
          quantity={quantity}
          isIncreaseDisabled={isPrepaidV2 || isIncreaseDisabled || isShuffle}
          isDecreaseDisabled={
            isPrepaidV2 || disableDecreaseButton() || isShuffle
          }
          available={isAvailable}
        />
        <Button
          style={{ marginLeft: '10%', width: '45%' }}
          onClick={handleSelect}
          filled
          disabled={!selected?.id || !selectedVariant?.available}
          fullWidth
        >
          {t('product_swap.update')}
        </Button>
      </QuantitySelector>
      {!isPrepaidV2 &&
        customerPortalSettings?.productChange !==
          PRODUCT_CHANGE_OPTIONS.SAME_PRODUCT && (
          <>
            <TextDivider>{t('subscription.or')}</TextDivider>
            <SwapButtonContainer>
              <ButtotText modifiers={['bold']} onClick={openProductSwap}>
                {t('subscription.swap_product_cta')}
              </ButtotText>
            </SwapButtonContainer>
          </>
        )}
    </SliderFooter>
  );
};

const ButtotText = styled(Paragraph)`
  cursor: pointer;
`;

const SliderFooter = styled.div.attrs({
  className: 'bundle-slider-footer',
})`
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  border-top: 1px solid #f1f2f3;
  background-color: inherit;
`;
const QuantitySelector = styled.div.attrs({
  className: 'bundle-quantity-selector',
})`
  width: 100%;
  margin-top: 20px;
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const SwapButtonContainer = styled.div.attrs({
  className: 'awt-swap-button-container',
})`
  padding: 20px 0px;
`;

const TextDivider = styled.div.attrs({
  className: 'awt-text-divider',
})`
  display: flex;
  flex-basis: 100%;
  align-items: center;
  text-transform: uppercase;
  font-size: 12px;
  color: var(--chakra-colors-gray-400);
  margin: 8px 0px;
  width: 100%;
  &::after {
    content: '';
    flex-grow: 1;
    background: #dfe3e8;
    height: 1px;
    font-size: 0px;
    line-height: 0px;
    margin: 0px 8px;
  }
  &::before {
    content: '';
    flex-grow: 1;
    background: #dfe3e8;
    height: 1px;
    font-size: 0px;
    line-height: 0px;
    margin: 0px 8px;
  }
`;

export const useVariantSliderSelector = (
  slidePanelAddon: GlobalProduct,
  selectedVariant,
  onSelect: (selectedAddon: { id: string; quantity: number }) => void,
  onConfirm: (selectedOption: MultiSelectOption) => void,
  openModal,
) => {
  const { t } = useTranslation();

  const selectedDefaultOption = slidePanelAddon.variantsOptions?.find(
    (variant) => variant.isSelected,
  );

  const initialOption =
    slidePanelAddon.variants.length > 1
      ? slidePanelAddon.variantsOptions?.find(
          (variant) => `${variant.id}` === selectedVariant?.id,
        )
      : ({
          label: slidePanelAddon.title,
          id: `${slidePanelAddon.variants[0].id}`,
          isSelected: true,
          type: 'option',
        } as MultiSelectOption);

  const [quantity, setQuantity] = useState<number>(
    slidePanelAddon.quantity || 1,
  );
  const [selected, setSelected] = useState<MultiSelectOption | undefined>(
    initialOption || selectedDefaultOption,
  );

  const buildInitialOptions = (slidePanelAddon, customerPortalSettings) => {
    const isPrepaidV2 = !!slidePanelAddon?.isPrepaidV2;
    const optionsLength = slidePanelAddon?.optionsAndValues?.length || 0;

    const isAllowProductChangeOnlyByVariantsOfSameProduct =
      customerPortalSettings?.productChange ===
      PRODUCT_CHANGE_OPTIONS.SAME_PRODUCT;
    const isAllowProductChangeByVariantsOfSameSellingPlan =
      customerPortalSettings?.productChange ===
      PRODUCT_CHANGE_OPTIONS.SAME_SUBSCRIPTION;

    const shouldHideSwapProductOption =
      isAllowProductChangeOnlyByVariantsOfSameProduct ||
      (isAllowProductChangeByVariantsOfSameSellingPlan &&
        !slidePanelAddon.sellingPlanId);

    const swapCtaOption =
      !isPrepaidV2 && !shouldHideSwapProductOption
        ? {
            id: `change-product-${slidePanelAddon.id}`,
            type: multiSelectType.OPTION,
            label: t('subscription.swap_product_cta'),
            customOnOptionClick: () => openProductSwap(),
          }
        : null;

    if (optionsLength === 1) {
      return [
        ...(slidePanelAddon?.optionsAndValues || []),
        swapCtaOption,
      ].filter(Boolean);
    }

    if (optionsLength > 1) {
      return [...(slidePanelAddon?.optionsAndValues || [])].filter(Boolean);
    }

    return [...(slidePanelAddon?.variantsOptions || [])];
  };

  const { customerPortalSettings } = useGlobalContext();

  const { isOpen, setIsOpen, handleOnOptionClicked, options } =
    useMultiSelectDropdown({
      initialOptions: buildInitialOptions(
        slidePanelAddon,
        customerPortalSettings,
      ) as MultiSelectOption[],
      isSingleSelect: false,
    });

  const openProductSwap = useCallback(() => {
    openModal(ProductSwapTypes.PRODUCT);
    setIsOpen(false);
  }, [openModal, setIsOpen]);

  const increase = () => setQuantity(quantity + 1);
  const decrease = () => setQuantity(quantity - 1);

  const onSelectVariantOption = () => {
    const isSingleVariant = slidePanelAddon.variants.length > 1;
    const selectedObject = options.filter(({ isSelected }) => isSelected);
    const selectedVariant = getVariantFromSelectedOptions(
      selectedObject,
      slidePanelAddon.variants,
    );

    const selectedVariantOption = {
      id: isSingleVariant
        ? selectedVariant?.id?.toString()
        : `${slidePanelAddon.variants[0].id}`,
      label: isSingleVariant ? selectedVariant.name : slidePanelAddon.title,
    };

    setSelected(selectedVariantOption);
    onConfirm(selectedVariantOption);
  };

  const handleConfirm = () => {
    onSelectVariantOption();
    setIsOpen(false);
  };

  const handleClose = () => {
    handleConfirm();
    setIsOpen(false);
  };

  const handleSelect = () => {
    onSelect({ id: selected?.id as string, quantity });
  };

  useDeepCompareEffect(() => {
    onSelectVariantOption();
  }, [options]);

  return {
    increase,
    decrease,
    handleSelect,
    handleConfirm,
    selected,
    isOpen,
    setIsOpen,
    handleOnOptionClicked,
    options,
    quantity,
    handleClose,
    openProductSwap,
  };
};

export default VariantSliderSelector;
